import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Divider,
  ListItemButton,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  ExitToApp as LogoutIcon,
  Queue as QueueIcon,
  AdminPanelSettings as AdminIcon,
  Support as SupportIcon,
  SmartToy as ChatbotIcon,
  Google as GoogleIcon,
  Campaign as CampaignIcon,
  Analytics as AnalyticsIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  TrendingUp as TrendingUpIcon,
  WhatsApp as WhatsAppIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 280;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Menu base para todos os usuários
  const baseMenuItems = [
    { 
      text: 'Dashboard', 
      icon: <DashboardIcon />, 
      path: '/admin',
      description: 'Visão geral do sistema'
    },
  ];

  // Menu de gerenciamento (admin e consultor)
  const managementMenuItems = [
    {
      text: 'Gerenciar Leads',
      icon: <PeopleIcon />,
      path: '/admin/leads',
      description: 'Visualizar e gerenciar leads'
    },
    {
      text: 'Disparo WhatsApp (Manual)',
      icon: <CampaignIcon />,
      path: '/admin/campanhas-manual',
      description: 'Importar leads e disparar mensagens manualmente'
    },
    {
      text: 'WhatsApp Web',
      icon: <WhatsAppIcon />,
      path: '/admin/whatsapp-web',
      description: 'Conexões, QR Code e fila de disparo'
    },
    {
      text: 'Inbox WhatsApp',
      icon: <SupportIcon />,
      path: '/admin/whatsapp-inbox',
      description: 'Conversas, sync e respostas dos leads'
    },
  ];

  // Menu específico para administradores
  const adminMenuItems = [
    { 
      text: 'Gerenciar Usuários', 
      icon: <AdminIcon />, 
      path: '/admin/usuarios',
      description: 'Criar e gerenciar usuários'
    },
    { 
      text: 'Config. Chatbot', 
      icon: <ChatbotIcon />, 
      path: '/admin/chatbot-configs',
      description: 'Configurar prompts do chatbot'
    },
  ];

  // Menu do Google MCP (apenas para administradores)
  const googleMCPMenuItems = [
    { 
      text: 'Google MCP Dashboard', 
      icon: <GoogleIcon />, 
      path: '/admin/google-mcp',
      description: 'Visão geral do Google MCP'
    },
    { 
      text: 'Campanhas Google', 
      icon: <CampaignIcon />, 
      path: '/admin/google-mcp/campaigns',
      description: 'Gerenciar campanhas Google Ads'
    },
    { 
      text: 'Leads Google', 
      icon: <TrendingUpIcon />, 
      path: '/admin/google-mcp/leads',
      description: 'Leads capturados do Google'
    },
    { 
      text: 'Analytics Google', 
      icon: <AnalyticsIcon />, 
      path: '/admin/google-mcp/analytics',
      description: 'Métricas e relatórios'
    },
    { 
      text: 'Alertas Google', 
      icon: <NotificationsIcon />, 
      path: '/admin/google-mcp/alerts',
      description: 'Configurar alertas'
    },
    { 
      text: 'Config. Google MCP', 
      icon: <SettingsIcon />, 
      path: '/admin/google-mcp/settings',
      description: 'Configurações do sistema'
    },
  ];

  // Menu específico para staff da VJR (Fase 3 -- onboarding de organizações-cliente)
  const platformAdminMenuItems = [
    {
      text: 'Organizações',
      icon: <BusinessIcon />,
      path: '/admin/organizations',
      description: 'Criar e gerenciar organizações-cliente'
    },
  ];

  // Menu específico para consultores
  const consultorMenuItems = [
    { 
      text: 'Fila de Atendimentos', 
      icon: <QueueIcon />, 
      path: '/fila-atendimentos',
      description: 'Gerenciar atendimentos'
    },
  ];

  // Combinar menus baseado no role do usuário
  const getMenuItems = () => {
    let items = [...baseMenuItems];
    
    // Adicionar menu de gerenciamento para todos
    items = [...items, ...managementMenuItems];
    
    if (user?.role === 'admin') {
      // Administradores têm acesso a todos os menus
      items = [...items, ...adminMenuItems, ...googleMCPMenuItems, ...consultorMenuItems];
    }
    
    if (user?.role === 'consultor') {
      items = [...items, ...consultorMenuItems];
    }

    if (user?.is_platform_admin) {
      items = [...items, ...platformAdminMenuItems];
    }

    return items;
  };

  const menuItems = getMenuItems();

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  const drawer = (
    <div>
      <Toolbar />
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" color="primary" fontWeight="bold">
          MedPrime
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {user?.role === 'admin' ? 'Administração' : 'Consultor'}
        </Typography>
      </Box>
      
      <List sx={{ pt: 1 }}>
        {menuItems.map((item, index) => (
          <React.Fragment key={item.text}>
            <ListItem disablePadding>
              <ListItemButton
                selected={isActiveRoute(item.path)}
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  mb: 0.5,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'primary.contrastText',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    minWidth: 40,
                    color: isActiveRoute(item.path) ? 'primary.contrastText' : 'inherit'
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  secondary={item.description}
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: isActiveRoute(item.path) ? 'bold' : 'normal',
                  }}
                  secondaryTypographyProps={{
                    fontSize: '0.75rem',
                    color: isActiveRoute(item.path) ? 'primary.contrastText' : 'text.secondary',
                  }}
                />
              </ListItemButton>
            </ListItem>
            
            {/* Adicionar divisor após Dashboard */}
            {index === 0 && (
              <Divider sx={{ my: 1, mx: 2 }} />
            )}
            
            {/* Adicionar divisor antes do Google MCP */}
            {item.text === 'Google MCP Dashboard' && (
              <Box sx={{ mt: 1, mb: 1 }}>
                <Divider sx={{ mx: 2 }} />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    px: 2, 
                    py: 1, 
                    display: 'block',
                    color: 'text.secondary',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: 1
                  }}
                >
                  Google MCP
                </Typography>
              </Box>
            )}
            
            {/* Adicionar divisor antes da Fila de Atendimentos */}
            {item.text === 'Fila de Atendimentos' && (
              <Divider sx={{ my: 1, mx: 2 }} />
            )}
          </React.Fragment>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuItems.find(item => isActiveRoute(item.path))?.text || 'MedPrime Benefícios'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ textAlign: 'right', mr: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                {user?.full_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.role === 'admin' ? 'Administrador' : 'Consultor'}
              </Typography>
            </Box>
            <Button
              color="inherit"
              startIcon={<LogoutIcon />}
              onClick={() => {
                logout();
                navigate('/login');
              }}
              sx={{ 
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              Sair
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid rgba(0, 0, 0, 0.12)',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: '#f5f5f5',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
